<script setup lang="ts">
import { ButtonColor, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import IconButton from '@/components/button/IconButton.vue';

import FileCheck from '@/icons/line/file-check.svg';
import FileError from '@/icons/line/file-error.svg';
import Trash from '@/icons/line/trash.svg';

type Props = {
  state: 'success' | 'loading' | 'error';
  accept?: string[];
  error?: string;
  multiple?: boolean;
  invalidFileNames?: string[];
};

const props = defineProps<Props>();
const model = defineModel<File | File[]>();
const fileDialog = useFileDialog();

function cancel() {
  fileDialog.reset();
  model.value = undefined;
}

function handleDelete(index?: number) {
  if (props.multiple && typeof index === 'number' && Array.isArray(model.value)) {
    const files = [...model.value];
    files.splice(index, 1);
    model.value = files.length ? files : undefined;
  } else {
    fileDialog.reset();
    model.value = undefined;
  }
}

function readableFileSize(fileSize: number) {
  const sizeInKb = fileSize / 1024;

  if (sizeInKb > 1024) {
    return `${(sizeInKb / 1024).toFixed(2)} mb`;
  } else {
    return `${sizeInKb.toFixed(2)} kb`;
  }
}

const files = computed(() => {
  if (!model.value) {
    return [];
  }
  return Array.isArray(model.value) ? model.value : [model.value];
});

const filesWithState = computed(() => {
  return files.value.map((file) => {
    const isInvalid = props.invalidFileNames?.includes(file.name) ?? false;
    const state = props.state === 'loading' ? 'loading' : isInvalid ? 'error' : 'success';

    const message = {
      loading: 'Uploading file',
      success: 'File format is correct',
      error: 'Unsupported file type'
    }[state];

    const icon = state === 'loading' ? null : state === 'success' ? FileCheck : FileError;

    return { file, state, message, icon };
  });
});
</script>

<template>
  <div v-if="filesWithState.length" class="flex flex-col gap-2">
    <div
      v-for="({ file, state, message, icon }, index) in filesWithState"
      :key="file.name + index"
      class="flex flex-row items-center justify-between rounded-lg border border-slate-200"
    >
      <div
        :class="[
          'flex flex-row overflow-hidden rounded-l-lg  border-l-4',
          {
            'border-slate-600': state === 'loading',
            'border-green-600': state === 'success',
            'border-red-700': state === 'error'
          }
        ]"
      >
        <div
          class="flex min-h-16 w-18 shrink-0 items-center justify-center border-r border-slate-200 bg-slate-50"
        >
          <div v-if="state === 'loading'" class="flex h-8 w-8 items-center justify-center">
            <div
              class="relative flex h-3 w-8 items-center overflow-hidden rounded-full border-2 border-slate-700/50"
            >
              <div class="animate-loading-bar absolute h-1 w-3 rounded-full bg-slate-700" />
            </div>
          </div>
          <component v-if="icon" :class="{ 'h-6 w-6 text-slate-700': true }" :is="icon" />
        </div>
        <div class="flex flex-col justify-center overflow-hidden px-3.5">
          <p class="truncate text-base font-semibold leading-5 text-slate-900">{{ file.name }}</p>
          <p v-if="message" class="divide-x divide-slate-200 text-sm font-medium leading-4">
            <span
              :class="{
                'text-slate-500': state === 'loading',
                'text-green-700': state === 'success',
                'text-red-700': state === 'error'
              }"
              class="block truncate md:inline md:pr-1"
              >{{ message }}</span
            >
            <span v-if="state === 'success'" class="hidden text-slate-600 md:inline md:pl-1">{{
              readableFileSize(file.size)
            }}</span>
          </p>
        </div>
      </div>

      <div v-if="state === 'loading'" class="flex flex-row items-center px-3">
        <Button :color="ButtonColor.slate" :variant="ButtonVariant.invisible" @click="cancel"
          >Cancel</Button
        >
      </div>
      <div v-else class="flex flex-row items-center px-3">
        <IconButton
          ariaLabel="Delete"
          variant="invisible"
          :icon="Trash"
          @click="handleDelete(index)"
        />
      </div>
    </div>
  </div>
</template>

<style>
@keyframes loading-bar-animation {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(300%);
  }
}

.animate-loading-bar {
  animation: loading-bar-animation 1.5s infinite ease-in-out;
}
</style>
